import {
  Col,
  Form,
  Input,
  message,
  Modal,
  Row,
  Typography,
} from 'antd';
import { postTipoDeCargo } from 'api/recaudacion/tiposDeCargo';
import React, { useRef, useState } from 'react';
import { onError } from 'utils/handlers';
import PropTypes from 'prop-types';
import Select from 'components/Select';
import { capitalize } from 'utils/normalizers';

const ModalDuplicarTipoDeCargo = ({
  visible,
  setVisible,
  loading,
  setLoading,
  tipoDeCargo,
  callback,
  periodosFiscales,
}) => {
  const [formCopy] = Form.useForm();
  const [copyConfirmed, setCopyConfirmed] = useState(false);
  const debounceCopy = useRef(null);

  const onCancelDuplicate = () => {
    setVisible(false);
    setCopyConfirmed(false);
    formCopy.resetFields();
  };

  const mapperId = (e) => (typeof e === 'number' ? e : e?.id);

  // logica para duplicar un tipo de cargo
  const handleCopy = async () => {
    try {
      await formCopy.validateFields();
      setLoading(true);
      const values = formCopy.getFieldsValue();
      const match = { ...tipoDeCargo };
      match.tipos_de_gasto = match.tipos_de_gasto.map(mapperId);
      match.tipos_de_recargo = match.tipos_de_recargo.map(mapperId);
      match.accesorios = match.accesorios.map(mapperId);
      match.tipos_de_actualizacion = match.tipos_de_actualizacion.map(mapperId);
      match.tipos_de_descuento = match.tipos_de_descuento.map(mapperId);
      match.variables = match.variables.map(mapperId);

      const saved = await postTipoDeCargo({
        ...match,
        ...values,
      });

      if (!saved) return;

      message.success('Tipo de cargo duplicado correctamente');
      onCancelDuplicate();
      await callback();
      setLoading(false);
    } catch (error) {
      onError(error, setLoading);
    } finally {
      setLoading(false);
    }
  };

  const rules = {
    required: [{ required: true }],
  };

  const generarClaveNueva = () => {
    const timestamp = Date.now().toString(36);
    const randomStr = Math.random().toString(36).substring(2, 6).toUpperCase();

    return `_${timestamp}_${randomStr}_DUP`.toUpperCase();
  };

  return (
    <Modal
      visible={visible}
      title={(
        <Typography.Title level={4}>
          Confirmar duplicado
        </Typography.Title>
          )}
      onOk={() => {
        if (!copyConfirmed) {
          setCopyConfirmed(true);
        } else {
          formCopy.submit();
        }
      }}
      onCancel={onCancelDuplicate}
      okText={copyConfirmed ? 'Duplicar' : 'Sí'}
      cancelText={copyConfirmed ? 'Cerrar' : 'No'}
      closable={false}
      centered
      confirmLoading={loading}
    >
      {!copyConfirmed && (
        <Typography.Text>
          ¿Estás seguro de querer duplicar el tipo de cargo
          {' '}
          {tipoDeCargo?.descripcion}
          ?
        </Typography.Text>
      )}
      {copyConfirmed && (
        <Form
          form={formCopy}
          onFinish={() => {
            if (debounceCopy.current) {
              clearTimeout(debounceCopy.current);
            }
            debounceCopy.current = setTimeout(() => {
              handleCopy();
            }, 500);
          }}
          layout="vertical"
          scrollToFirstError
          initialValues={{
            clave: generarClaveNueva(),
            descripcion: `${tipoDeCargo?.descripcion} (Copia)`.toUpperCase(),
          }}
        >
          <Row>
            <Col span={24}>
              <Form.Item
                label="Clave"
                name="clave"
                rules={rules.required}
                normalize={capitalize}
              >
                <Input allowClear />
              </Form.Item>
            </Col>
            <Col span={24}>
              <Form.Item
                label="Descripción"
                name="descripcion"
                rules={rules.required}
                normalize={capitalize}
              >
                <Input.TextArea
                  allowClear
                  autoSize={{ minRows: 3, maxRows: 5 }}
                  maxLength={255}
                />
              </Form.Item>
            </Col>
            <Col span={24}>
              <Form.Item
                label="Periodo Fiscal donde se aplicará"
                name="periodo_fiscal"
                rules={rules.required}
              >
                <Select
                  dataSource={periodosFiscales}
                  render={(pf) => (pf.vigente ? `${pf.periodo} - (vigente)` : pf.periodo)}
                />
              </Form.Item>
            </Col>
          </Row>
        </Form>
      )}
    </Modal>
  );
};

ModalDuplicarTipoDeCargo.propTypes = {
  visible: PropTypes.bool.isRequired,
  setVisible: PropTypes.func.isRequired,
  initialValues: PropTypes.shape({
    clave: PropTypes.string,
    periodo_fiscal: PropTypes.number,
  }),
  loading: PropTypes.bool.isRequired,
  setLoading: PropTypes.func.isRequired,
  tipoDeCargo: PropTypes.shape({
    id: PropTypes.number,
    clave: PropTypes.string,
    descripcion: PropTypes.string,
  }),
  callback: PropTypes.func.isRequired,
  periodosFiscales: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.number,
    periodo: PropTypes.string,
    vigente: PropTypes.bool,
  })).isRequired,
};

ModalDuplicarTipoDeCargo.defaultProps = {
  initialValues: {},
  tipoDeCargo: {},
};

export default ModalDuplicarTipoDeCargo;
